/**
 * Client-side guard for photo-upload batches: caps the number of images a user
 * can add in one go and rejects oversized files before they are uploaded. Pure
 * and deterministic (reads only `.size` off each file) so it can be unit-tested
 * without the DOM. The server enforces the same byte cap in
 * Mutations::GenerateDirectUpload; this is for immediate UX feedback.
 */

export const MAX_IMAGES_PER_UPLOAD = 30;
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024; // 15 MB

export interface UploadBatchLimits {
  maxCount?: number;
  maxBytes?: number;
}

export interface SizedFile {
  size: number;
  name?: string;
}

export interface UploadBatchValidation<T extends SizedFile> {
  accepted: T[];
  rejectedTooLarge: T[];
  rejectedOverCount: T[];
  error: string | null;
}

export function validateImageUploadBatch<T extends SizedFile>(
  files: T[],
  existingCount = 0,
  limits: UploadBatchLimits = {}
): UploadBatchValidation<T> {
  const maxCount = limits.maxCount ?? MAX_IMAGES_PER_UPLOAD;
  const maxBytes = limits.maxBytes ?? MAX_IMAGE_BYTES;

  const withinSize: T[] = [];
  const rejectedTooLarge: T[] = [];
  for (const file of files) {
    (file.size > maxBytes ? rejectedTooLarge : withinSize).push(file);
  }

  const remainingSlots = Math.max(0, maxCount - existingCount);
  const accepted = withinSize.slice(0, remainingSlots);
  const rejectedOverCount = withinSize.slice(remainingSlots);

  const messages: string[] = [];
  if (rejectedTooLarge.length) {
    const mb = Math.round(maxBytes / (1024 * 1024));
    messages.push(
      `${rejectedTooLarge.length} file(s) exceed the ${mb} MB limit and were skipped.`
    );
  }
  if (rejectedOverCount.length) {
    messages.push(
      `You can upload at most ${maxCount} images at once; ${rejectedOverCount.length} were skipped.`
    );
  }

  return {
    accepted,
    rejectedTooLarge,
    rejectedOverCount,
    error: messages.length ? messages.join(' ') : null,
  };
}
